// src/components/RdoPdf.jsx
import jsPDF from 'jspdf';

class RdoPdf {
  static CONFIG = {
    mL: 14, mR: 14, mT: 14, mB: 14,
    pW: 210, pH: 297,
    corAzul:   [21, 101, 192],
    corCinza:  [100, 100, 100],
    corPreto:  [30, 30, 30],
    corBorda:  [220, 220, 220],
    corFundo:  [248, 249, 250],
    corHeader: [21, 101, 192],
  };

  // ── Montar PDF (reutilizável) ───────────────────────────────
  // Retorna o objeto jsPDF já montado (sem baixar/imprimir).
  static async montarPdf(projetoNome, dataOriginal, dados) {
    if (!dados) throw new Error('Dados do RDO não informados.');

    const pdf  = new jsPDF('p', 'mm', 'a4');
    const C    = this.CONFIG;
    const util = C.pW - C.mL - C.mR;
    const limiteY = C.pH - C.mB - 8;

    pdf.setLineHeightFactor(1.35);
    const lh = (size) => (size * 1.35) / pdf.internal.scaleFactor;

    let y = C.mT;
    let pagina = 1;

    const novaPagina = () => {
      this.rodape(pdf, pagina);
      pdf.addPage();
      pagina++;
      y = C.mT;
    };
    const verificarEspaco = (altura) => {
      if (y + altura > limiteY) novaPagina();
    };
    const secao = (titulo, minEspaco = 20) => {
      verificarEspaco(minEspaco);
      this.secaoTitulo(pdf, titulo, y, util, C);
      y += 8;
    };
    const vazio = (msg) => {
      pdf.setTextColor(...C.corCinza);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(msg, C.mL + 2, y + 4);
      y += 9;
    };

    const blocoTexto = (texto, { fill = [255, 255, 255], draw = C.corBorda, cor = C.corPreto } = {}) => {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const h1 = lh(9);
      const linhas = pdf.splitTextToSize(texto, util - 6);
      let i = 0;
      while (i < linhas.length) {
        let disp = Math.floor((limiteY - y - 4) / h1);
        if (disp < 1) { novaPagina(); continue; }
        const chunk = linhas.slice(i, i + disp);
        const h = chunk.length * h1 + 4;
        pdf.setFillColor(...fill);
        pdf.setDrawColor(...draw);
        pdf.rect(C.mL, y, util, h, 'FD');
        pdf.setTextColor(...cor);
        pdf.text(chunk, C.mL + 3, y + 3.5 + h1 * 0.5);
        y += h;
        i += chunk.length;
      }
      y += 2;
    };

    const tabela = (headers, cols, rows, x = C.mL, w = util) => {
      const desenharCab = () => {
        this.cabecalhoTabela(pdf, headers, x, y, w, C, cols);
        y += 6;
      };
      verificarEspaco(6 + 5.5 * Math.min(rows.length, 2));
      desenharCab();
      rows.forEach((r, i) => {
        if (y + 5.5 > limiteY) { novaPagina(); desenharCab(); }
        this.linhaTabela(pdf, r, x, y, w, C, i, cols);
        y += 5.5;
      });
    };

    const galeria = async (fotos, porLinha) => {
      const gap = 4;
      const w = (util - gap * (porLinha - 1)) / porLinha;
      const boxH = w * 0.75;
      for (let i = 0; i < fotos.length; i += porLinha) {
        const linha = fotos.slice(i, i + porLinha);
        const imgs = await Promise.all(linha.map(f => this.carregarImagem(f.src).catch(() => null)));
        verificarEspaco(boxH + 11);
        linha.forEach((f, k) => {
          const x = C.mL + k * (w + gap);
          pdf.setDrawColor(...C.corBorda);
          pdf.rect(x, y, w, boxH, 'S');
          const im = imgs[k];
          if (im) {
            const r = Math.min(w / im.w, boxH / im.h);
            const iw = im.w * r, ih = im.h * r;
            pdf.addImage(im.data, 'JPEG', x + (w - iw) / 2, y + (boxH - ih) / 2, iw, ih, undefined, 'FAST');
          } else {
            pdf.setFontSize(8);
            pdf.setTextColor(...C.corCinza);
            pdf.text('Imagem indisponível', x + 3, y + boxH / 2);
          }
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...C.corCinza);
          let ty = y + boxH + 3.2;
          if (f.legenda) {
            pdf.text(pdf.splitTextToSize(f.legenda, w)[0], x, ty);
            ty += 3.2;
          }
          if (f.info) pdf.text(pdf.splitTextToSize(f.info, w)[0], x, ty);
        });
        y += boxH + 11;
      }
    };

    // ── Cabeçalho ──────────────────────────────────────────
    pdf.setFillColor(...C.corHeader);
    pdf.roundedRect(C.mL, y, util, 22, 3, 3, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(dados.titulo || 'Diário de Obra', C.mL + 5, y + 9);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(dados.subtitulo || '', C.mL + 5, y + 17);
    y += 27;

    // ── Informações gerais ─────────────────────────────────
    this.secaoTitulo(pdf, 'Informações Gerais', y, util, C);
    y += 8;

    this.campos(pdf, [
      { label: 'Engenheiro Responsável', valor: dados.engenheiro },
      { label: 'Responsável pelo Preenchimento', valor: dados.responsavel },
    ], y, util, C);
    y += 15;

    this.campos(pdf, [
      { label: 'Início da Obra',   valor: this.fmtData(dados.inicioObra) },
      { label: 'Término Previsto', valor: this.fmtData(dados.terminoObra) },
      { label: 'Atraso (dias)',    valor: String(dados.atraso || '0') },
    ], y, util, C);
    y += 15;

    this.campos(pdf, [
      { label: 'Dia de feriado',               valor: dados.feriado ? 'Sim' : 'Não' },
      { label: 'Dia compensado em outra data', valor: dados.diaCompensado ? 'Sim' : 'Não' },
    ], y, util, C);
    y += 15;

    // ── Clima ──────────────────────────────────────────────
    secao('Clima', 40);

    if (dados.usarNovoLayoutClima) {
      const rows = (dados.clima || []).map(c => [
        c.hora,
        c.condicao || 'Aguardando',
        c.temperatura != null ? `${Math.round(c.temperatura)}°C` : '—',
      ]);
      tabela(['Horário', 'Condição', 'Temperatura'], [30, 90, 62], rows);
    } else {
      const rotulo = (v) => v === 'chuvoso' ? 'Chuvoso' : v === 'seco' ? 'Ensolarado' : '—';
      tabela(['Período', 'Condição'], [40, 142], [
        ['Manhã', rotulo(dados.climaManha)],
        ['Tarde', rotulo(dados.climaTarde)],
      ]);
    }
    y += 3;

    const op = (v) => v === 'sim' ? 'Sim' : v === 'nao' ? 'Não' : '—';
    tabela(['Período', 'Obra Operacional'], [40, 142], [
      ['Manhã', op(dados.obraOpManha)],
      ['Tarde', op(dados.obraOpTarde)],
    ]);
    y += 4;

    // ── Atividades ─────────────────────────────────────────
    secao('Atividades Executadas', 25);

    const atividades = (dados.atividades || []).filter(
      a => (a.descricao || '').trim() || (a.fotos || []).length
    );
    if (!atividades.length) vazio('Nenhuma atividade registrada.');

    for (const at of atividades) {
      verificarEspaco(20);
      pdf.setFillColor(...C.corFundo);
      pdf.setDrawColor(...C.corBorda);
      pdf.rect(C.mL, y, util, 7, 'FD');
      pdf.setTextColor(...C.corAzul);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(at.pavimento, C.mL + 3, y + 4.8);
      y += 7;

      if ((at.descricao || '').trim()) blocoTexto(at.descricao.trim());

      if (at.fotos?.length) {
        y += 1;
        await galeria(
          at.fotos.map(f => ({
            src: f.src,
            legenda: f.legenda,
            info: `${this.fmtData(dados.dataRdo)} · ${at.pavimento}`,
          })),
          3
        );
      }
      y += 2;
    }

    // ── Efetivo ────────────────────────────────────────────
    const temEf = (e) => (e.funcao || '').trim() || Number(e.total) || Number(e.presentes);
    const ep = (dados.efetivoProprio || []).filter(temEf);
    const et = (dados.efetivoTerc || []).filter(temEf);

    secao('Efetivo', 35);
    if (!ep.length && !et.length) {
      vazio('Nenhum efetivo registrado.');
    } else {
      const colW = (util - 6) / 2;
      const xB = C.mL + colW + 6;
      const cols = [55, 22, 23];
      const cab = () => {
        this.miniTitulo(pdf, 'Efetivo Próprio', C.mL, y, colW, C);
        this.miniTitulo(pdf, 'Efetivo Terceirizado', xB, y, colW, C);
        y += 7;
        this.cabecalhoTabela(pdf, ['Função', 'Total', 'Presentes'], C.mL, y, colW, C, cols);
        this.cabecalhoTabela(pdf, ['Empresa', 'Total', 'Presentes'], xB, y, colW, C, cols);
        y += 6;
      };
      cab();
      const n = Math.max(ep.length, et.length);
      for (let i = 0; i < n; i++) {
        if (y + 5.5 > limiteY) { novaPagina(); cab(); }
        const a = ep[i], b = et[i];
        if (a) this.linhaTabela(pdf, [a.funcao, a.total || '0', a.presentes || '0'], C.mL, y, colW, C, i, cols);
        if (b) this.linhaTabela(pdf, [b.funcao, b.total || '0', b.presentes || '0'], xB, y, colW, C, i, cols);
        y += 5.5;
      }
      y += 4;
    }

    // ── Materiais ──────────────────────────────────────────
    secao('Recebimento de Materiais', 25);
    const mats = (dados.materiais || []).filter(m => m.codigo || m.descricao || m.quantidade);
    if (mats.length) {
      tabela(
        ['Código', 'Descrição', 'Unidade', 'Qtd', 'Empresa'],
        [18, 55, 20, 18, 40],
        mats.map(m => [m.codigo, m.descricao, m.unidade, m.quantidade, m.empresa])
      );
      y += 4;
    } else vazio('Nenhum material registrado.');

    // ── Equipamentos ───────────────────────────────────────
    secao('Equipamentos', 25);
    const eqs = (dados.equipamentos || []).filter(e => e.codigo || e.descricao);
    if (eqs.length) {
      tabela(
        ['Código', 'Descrição', 'Total', 'Em Uso'],
        [20, 100, 22, 22],
        eqs.map(e => [e.codigo, e.descricao, e.total, e.em_uso])
      );
      y += 4;
    } else vazio('Nenhum equipamento registrado.');

    // ── Intercorrências ────────────────────────────────────
    secao('Intercorrências', 25);
    if ((dados.intercorrencias || '').trim()) {
      blocoTexto(dados.intercorrencias.trim(), {
        fill: [255, 251, 235], draw: [245, 158, 11], cor: [120, 60, 0],
      });
      y += 2;
    } else vazio('Nenhuma intercorrência registrada.');

    // ── Fotos gerais ───────────────────────────────────────
    if (dados.fotos?.length) {
      secao('Fotos', 60);
      await galeria(dados.fotos.map(src => ({ src })), 2);
    }

    this.rodape(pdf, pagina);

    pdf.setProperties({
      title:   `RDO - ${projetoNome} - ${this.fmtData(dataOriginal)}`,
      subject: 'Relatório Diário de Obra',
      author:  'OnBox',
      creator: 'OnBox',
    });

    return pdf;
  }

  // ── Exportar (download) ────────────────────────────────────
  static async exportar(projetoNome, dataOriginal, dados) {
    let loadingMsg = null;
    try {
      loadingMsg = this.criarMensagemCarregamento();
      document.body.appendChild(loadingMsg);

      const pdf = await this.montarPdf(projetoNome, dataOriginal, dados);

      const blob          = pdf.output('blob');
      const nomeSeguro    = this.sanitizarNomeArquivo(projetoNome);
      const dataFormatada = this.formatarDataParaNomeArquivo(dataOriginal);
      this.baixarArquivo(blob, `RDO - ${nomeSeguro} - ${dataFormatada}.pdf`);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert(`Erro ao gerar PDF: ${err.message}`);
    } finally {
      if (loadingMsg) this.removerMensagemCarregamento(loadingMsg);
    }
  }

  // ── Imprimir (mesmo PDF, fidelidade total) ─────────────────
  static async imprimir(projetoNome, dataOriginal, dados) {
    let loadingMsg = null;
    try {
      loadingMsg = this.criarMensagemCarregamento();
      document.body.appendChild(loadingMsg);

      const pdf = await this.montarPdf(projetoNome, dataOriginal, dados);

      pdf.autoPrint(); // dispara window.print() no PDF ao abrir
      const blobUrl = pdf.output('bloburl');

      const win = window.open(blobUrl, '_blank');
      if (!win) {
        alert('Permita pop-ups no navegador para imprimir o RDO.');
      }

      // Libera o blob após um tempo (o navegador já carregou o PDF)
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch {}
      }, 60_000);
    } catch (err) {
      console.error('Erro ao imprimir PDF:', err);
      alert(`Erro ao imprimir: ${err.message}`);
    } finally {
      if (loadingMsg) this.removerMensagemCarregamento(loadingMsg);
    }
  }

  // ── Helpers de layout ───────────────────────────────────────

  static secaoTitulo(pdf, texto, y, util, C) {
    pdf.setFillColor(...C.corAzul);
    pdf.roundedRect(C.mL, y, util, 6, 1, 1, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(texto, C.mL + 3, y + 4.3);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...C.corPreto);
  }

  static miniTitulo(pdf, texto, x, y, w, C) {
    pdf.setFillColor(232, 240, 254);
    pdf.roundedRect(x, y, w, 6, 1, 1, 'F');
    pdf.setTextColor(...C.corAzul);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(texto, x + 3, y + 4.3);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...C.corPreto);
  }

  static campos(pdf, campos, y, util, C) {
    const gap = 6;
    const w = (util - gap * (campos.length - 1)) / campos.length;
    campos.forEach((c, i) => {
      const x = C.mL + i * (w + gap);
      pdf.setFillColor(...C.corFundo);
      pdf.setDrawColor(...C.corBorda);
      pdf.roundedRect(x, y, w, 12, 1, 1, 'FD');
      pdf.setFontSize(7.5);
      pdf.setTextColor(...C.corCinza);
      pdf.setFont('helvetica', 'bold');
      pdf.text(c.label, x + 3, y + 4.5);
      pdf.setFontSize(9);
      pdf.setTextColor(...C.corPreto);
      pdf.setFont('helvetica', 'normal');
      const t = pdf.splitTextToSize(String(c.valor || '—'), w - 6)[0];
      pdf.text(t, x + 3, y + 10);
    });
  }

  static cabecalhoTabela(pdf, headers, x, y, w, C, cols) {
    const total = cols.reduce((a, b) => a + b, 0);
    const escala = w / total;
    pdf.setFillColor(230, 238, 255);
    pdf.setDrawColor(...C.corBorda);
    pdf.rect(x, y, w, 5.5, 'FD');
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...C.corAzul);
    let cx = x;
    headers.forEach((h, i) => {
      pdf.text(h, cx + 2, y + 4);
      cx += cols[i] * escala;
    });
  }

  static linhaTabela(pdf, cells, x, y, w, C, rowIdx, cols) {
    const total = cols.reduce((a, b) => a + b, 0);
    const escala = w / total;
    const par = rowIdx % 2 === 0;
    pdf.setFillColor(par ? 255 : 248, par ? 255 : 249, par ? 255 : 250);
    pdf.setDrawColor(235, 235, 235);
    pdf.rect(x, y, w, 5.5, 'FD');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...C.corPreto);
    let cx = x;
    cells.forEach((cell, i) => {
      const cw = cols[i] * escala;
      const texto = pdf.splitTextToSize(String(cell ?? ''), cw - 3);
      pdf.text(texto[0] || '', cx + 2, y + 4);
      cx += cw;
    });
  }

  static rodape(pdf, pagina) {
    const C = this.CONFIG;
    pdf.setFontSize(7.5);
    pdf.setTextColor(160, 160, 160);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Página ${pagina} — OnBox · Relatório Diário de Obra`, C.pW / 2, C.pH - 6, { align: 'center' });
    pdf.setDrawColor(210, 210, 210);
    pdf.line(C.mL, C.pH - 8, C.pW - C.mR, C.pH - 8);
  }

  // ── Utilitários ─────────────────────────────────────────────

  static carregarImagem(url, maxLado = 1000) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const esc = Math.min(1, maxLado / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.round(img.naturalWidth * esc);
        const h = Math.round(img.naturalHeight * esc);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ data: canvas.toDataURL('image/jpeg', 0.8), w, h });
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  static fmtData(str) {
    if (!str) return '—';
    const p = String(str).split('T')[0].split('-');
    if (p.length !== 3) return str;
    return `${p[2]}/${p[1]}/${p[0]}`;
  }

  static formatarDataParaNomeArquivo(data) {
    if (!data) return 'sem_data';
    const p = String(data).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : 'sem_data';
  }

  static sanitizarNomeArquivo(nome) {
    if (!nome) return 'projeto';
    return nome.replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
  }

  static baixarArquivo(blob, nome) {
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
  }

  static criarMensagemCarregamento() {
    const el = document.createElement('div');
    el.id = 'rdo-pdf-loading';
    el.style.cssText = `
      position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
      background:#fff;padding:20px 40px;border-radius:8px;
      box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:10000;
      font-size:16px;font-weight:bold;text-align:center;
    `;
    el.innerHTML = `<div>Gerando PDF...</div><div style="font-size:12px;font-weight:normal;margin-top:8px;color:#666;">Isso pode levar alguns segundos</div>`;
    return el;
  }

  static removerMensagemCarregamento(el) {
    try { if (el?.parentNode) el.parentNode.removeChild(el); } catch {}
  }
}

export default RdoPdf;